import type { User } from '@takeshape/schema';
import { createRoutine as createNewRoutine } from 'app/common/utils/new-routines';
import { createAction } from 'typesafe-actions';
import {
  EMAIL_VALIDATED,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  RESPOND_TO_CLI_ERROR,
  SET_ACCESS_TOKEN,
  SET_CLI_LOGIN_TOKEN,
  SET_EMAIL_REQUIRES_EMAIL_VALIDATION
} from './action-types';

export type UserValues = {
  data: User;
};

export type Meta = {
  silent?: boolean;
  redirectOnSuccess?: string;
  executeRecaptcha?: () => Promise<string>;
};

export type AuthAction = {
  type: string;
  payload?: UserValues;
  meta?: Meta;
  token?: string;
};

export const getUser = createNewRoutine('auth/GET_USER')<
  undefined,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export type CreateUserRequestedPayload = {
  user: {
    fullName: string;
    email: string;
    password: string;
  };
  inviteCode?: string;
  executeRecaptcha: () => void;
};

export const createUser = createNewRoutine('auth/CREATE_USER')<
  Meta,
  CreateUserRequestedPayload,
  undefined,
  UserValues,
  Error,
  undefined
>();

export type JwtLoginRequestedPayload = {
  jwt: {
    provider: 'google';
    token: string;
  };
  tracking: Record<string, unknown>;
  inviteCode?: string;
};

export type PasswordLoginRequestedPayload = {
  email: string;
  password: string;
  inviteCode?: string;
};

export type LoginRequestedPayload = JwtLoginRequestedPayload | PasswordLoginRequestedPayload;

export const loginUser = createNewRoutine('auth/LOGIN_USER')<
  Meta,
  LoginRequestedPayload,
  undefined,
  UserValues,
  string,
  undefined
>();

export type UpdateUserAction = {
  type: 'auth/UPDATE_USER_REQUESTED';
  payload?: Partial<User>;
  meta?: Meta;
};

export type UpdateUserFulfilledAction = {
  type: 'auth/UPDATE_USER_FULFILLED';
  payload: Partial<User>;
  meta: Meta;
};

export function updateUser(values: Partial<User>, silent: boolean): UpdateUserAction {
  return {
    type: 'auth/UPDATE_USER_REQUESTED',
    payload: values,
    meta: {
      silent
    }
  };
}

export const logoutUser = createNewRoutine('auth/LOGOUT_USER')<
  undefined,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export function forgotPassword({
  values,
  redirectOnSuccess,
  executeRecaptcha
}: {
  values: UserValues;
  redirectOnSuccess: string;
  executeRecaptcha: () => Promise<string>;
}): AuthAction {
  return {
    type: `${FORGOT_PASSWORD}_REQUESTED`,
    payload: values,
    meta: {
      redirectOnSuccess,
      executeRecaptcha
    }
  };
}

export function resetPassword({
  values,
  token,
  redirectOnSuccess
}: {
  values: { password: string };
  token: string;
  redirectOnSuccess: string;
}) {
  return {
    type: `${RESET_PASSWORD}_REQUESTED`,
    payload: values,
    token,
    meta: {
      redirectOnSuccess
    }
  };
}

export const changePassword = createNewRoutine('auth/CHANGE_PASSWORD')<
  { password: string; newPassword: string },
  undefined,
  undefined,
  undefined,
  undefined,
  undefined
>();

export const setRespondToCliError = createAction(RESPOND_TO_CLI_ERROR)();

export const setCliLoginToken = createAction(SET_CLI_LOGIN_TOKEN)<string | null>();

export type PermissionsUpdatedPayload = {
  projectId: string;
  userId: string;
  role: string;
};

export const permissionsUpdated = createAction('auth/PERMISSIONS_UPDATED')<PermissionsUpdatedPayload>();

export const setAccessToken = createAction(SET_ACCESS_TOKEN)<string | null>();

export const setUserRequiresEmailValidation = createAction(SET_EMAIL_REQUIRES_EMAIL_VALIDATION)<boolean>();

export const emailValidated = createAction(EMAIL_VALIDATED)();
